import { ShareRepresentation, ShareStatus, UpdateShareData } from '@common/api';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { Connection, Copy, Delete, Link, RefreshAlt } from '@components/icons/FarseerIcons';
import { useModal } from '@components/modal/ModalProvider';
import { useGroups } from '@data/hooks/usersAndGroups';
import { useShareActions } from '@data/share/shareActions';
import { createEntryLinkPath } from '@data/share/util';
import { RegeneratePublicKeysConfirmationModal } from '@features/assignments/RegeneratePublicKeysConfirmationModal';
import { useOpenFolderItemReference } from '@hooks/useOpenFolderItemReference';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import { Update } from '@use-gpu/state';
import toast from 'react-hot-toast';
import { ShareStatusMenuItems } from './ShareStatus';

interface ActionsMenuProps {
    items: ShareRepresentation[];
    onChange: (updates: Update<ShareRepresentation>[]) => void;
    areFolders?: boolean;
    areSheets?: boolean;
    isGlobal?: boolean;
}

export const ShareTableActions = ({
    items,
    onChange,
    areFolders,
    areSheets,
    isGlobal,
}: ActionsMenuProps & { items: ShareRepresentation[] }) => {
    const { LL } = useI18nContext();
    const { showModal } = useModal();
    const { user, tenant } = useAuth();
    const groups = useGroups();
    const {
        api: { updateBatch, deleteBatch, applyToDescendants },
    } = useShareActions();
    const { openFolderItemReference } = useOpenFolderItemReference();

    if (!user || !groups || items.length === 0) return null;

    const allHavePublicKey = items.every((i) => i.publicKey.active);
    const allCanRefresh = items.every((i) => i.expirationTime.active);

    const ids = items.map((i) => i.id);

    return (
        <>
            {isGlobal ? (
                <>
                    <ContextMenu.Item
                        item={{
                            label: LL.OPEN(),
                            icon: Link,
                            disabled: items.length !== 1, // open only works for single selection
                            action: () => openFolderItemReference(items[0].itemNode),
                        }}
                    />
                </>
            ) : areFolders ? (
                <>
                    <ContextMenu.Item
                        item={{
                            label: LL.COPY_SHARE_IN_FOLDER(),
                            icon: Copy,
                            disabled: items.length === 0,
                            action: async () => {
                                // TODO
                                for (const item of items) {
                                    await applyToDescendants({
                                        itemNodeId: item.itemNode.id,
                                        shareIds: [item.id],
                                        clearExistingShares: false,
                                    });
                                }
                                toast.success(LL.SHARE_COPIED());
                            },
                        }}
                    />
                </>
            ) : null}

            {areSheets ? <ShareStatusMenuItems divider items={items} onChange={onChange} /> : null}

            {allHavePublicKey ? (
                <>
                    <ContextMenu.Item
                        item={{
                            icon: Connection,
                            label: LL.REGENERATE_LINK(),
                            disabled: items.length === 0,
                            action: () => showModal(<RegeneratePublicKeysConfirmationModal shareIds={ids} />),
                        }}
                    />
                    <ContextMenu.Item
                        item={{
                            icon: Copy,
                            label: LL.COPY_LINK(),
                            disabled: !tenant,
                            action: () => {
                                if (!tenant) return;
                                const links = items.map((item) => createEntryLinkPath(item.publicKey.value, tenant));
                                window.navigator.clipboard.writeText(links.join('\n'));
                                toast.success(LL.PUBLIC_ASSIGNMENT_LINK_COPIED_TO_CLIPBOARD());
                            },
                        }}
                    />
                    <ContextMenu.Divider />
                </>
            ) : null}

            {allCanRefresh ? (
                <ContextMenu.Item
                    item={{
                        icon: RefreshAlt,
                        label: LL.REFRESH(),
                        disabled: items.length === 0,
                        action: () => updateBatch(items.map((item) => refreshRequest(item))),
                    }}
                />
            ) : null}

            {allCanRefresh ? <ContextMenu.Divider /> : null}

            <ContextMenu.Item
                item={{
                    icon: Delete,
                    label: LL.DELETE(),
                    disabled: items.length === 0,
                    action: () => deleteBatch(ids),
                }}
            />
        </>
    );
};

export function refreshRequest(share: ShareRepresentation): UpdateShareData {
    const daysToAdd = 7;
    const newExpirationDate = new Date(Date.now());
    newExpirationDate.setDate(newExpirationDate.getDate() + daysToAdd);
    return {
        ...share,
        expirationTime: { active: true, value: newExpirationDate },
        status: share.status != null ? ShareStatus.Shared : null,
    };
}
